import {gql} from "@apollo/client";
import {faPen, faTrash} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import AppLink from "components/common/AppLink";
import ComponentState, {ComponentStateTypes} from "components/common/ComponentState";
import ConfirmationModal from "components/common/confirmation/ConfirmationModal";
import If from "components/common/If";
import Translate from "components/common/Translate";
import {graph} from "config/axios-context";
import {AppRoutes} from "config/router";
import {useEffect, useState} from "react";
import { useSearchParams} from "react-router-dom";
import {NotNull} from "types/common-types";
import {HarvestTemplateDatalistQuery, HarvestTemplateDatalistQueryVariables, SortEnumType} from "types/graphql";
import {Roles} from "utilities/app-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import {deleteHarvestTemplateMutation} from "../../../../../types/agriculture/harvest-agriculture-work-types";
import Button from "components/common/button/Button";

const DELETE_HASH = '#delete-harvest-datalist';

const datalistQuery = gql`
query HarvestTemplateDatalist($where: HarvestAgricultureWorkTemplateFilterInput, $order: [HarvestAgricultureWorkTemplateSortInput!], $take: Int, $skip: Int) {
    harvestAgricultureWorkTemplates {
      items(where: $where, order: $order, take: $take, skip: $skip) {
        id
        name
      }
    } 
  }
`;

type HarvestTemplateDatalistProps = {
    newUrl?: string | AppRoutes
    onClickUrl?: (item: NotNull<NotNull<HarvestTemplateDatalistQuery['harvestAgricultureWorkTemplates']>['items']>[0]) => string
}

const HarvestTemplateDatalist = (props: HarvestTemplateDatalistProps) => {
    const [state, setState] = useState<ComponentStateTypes>('loading');
    const [data, setData] = useState<NotNull<HarvestTemplateDatalistQuery['harvestAgricultureWorkTemplates']>['items']>();
    const context = useAppContext();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async () => {
        setState('loading');

        try {
            const response = await graph<HarvestTemplateDatalistQuery, HarvestTemplateDatalistQueryVariables>({
                query: datalistQuery,
                variables: {
                    // where: {
                    //     harvests: {
                    //         any: true
                    //     }
                    // }, TODO: add harvest filter
                    order: [{
                        name: SortEnumType.Asc
                    }]
                }
            });

            if (response.errors) {
                throw response.errors;
            }

            if (!response.data?.harvestAgricultureWorkTemplates?.items.length) {
                setData([]);
                setState('');
            }


            setState('');
            setData(response.data!.harvestAgricultureWorkTemplates!.items);
        } catch (error) {
            setState('error');
            context.notify('error', 'error.loading-data')
            throw error;
        }
    }

    const onDelete = async () => {
        try {
            await deleteHarvestTemplateMutation(Number(context.modal.getData(DELETE_HASH)));
            await loadData();
        } catch (error) {
            context.notify('error', 'request.delete.error');
            throw error;
        }
    }

    return (<>
            <ul className=" px-2 md:px-0">
                <If condition={context.userHasRole(Roles.AGRICULTURE_WORK_WRITE)}>
                    <li className="border-b theme-border pb-1 py-4 md:py-2">
                        <AppLink to={props.newUrl || AppRoutes.TEMPLATE_HARVEST_FORM}>
                            <Translate i18nKey="button.add-new"/>
                        </AppLink>
                    </li>
                </If>
                <ComponentState state={state}>
                    <>
                        {data?.map(i => {
                            return (
                                <li key={i!.id} className="border-b border-transparent hover:border-primary">
                                    <If condition={!props.onClickUrl}>
                                        <div className="flex">
                                            <div className="w-full my-auto">
                                                {i?.name}
                                            </div>
                                            <If condition={context.userHasRole(Roles.AGRICULTURE_WORK_WRITE)}>
                                                <div>
                                                    <div className="grid-cols-2 gap-4 grid text text-center">
                                                        <div className="p-2 my-auto text-center">
                                                            <AppLink className="link"
                                                                     to={AppRoutes.TEMPLATE_HARVEST_EDIT}
                                                                     params={{id: i?.id}}>
                                                                <FontAwesomeIcon icon={faPen}/>
                                                            </AppLink>
                                                        </div>
                                                        <div className="p-2 my-auto text-center">
                                                            <Button theme="link" className="text-danger" title="button.delete" onClick={() => context.modal.open(DELETE_HASH, i.id)}>
                                                                <FontAwesomeIcon icon={faTrash}/>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </If>
                                        </div>
                                    </If>
                                    <If condition={props.onClickUrl}>
                                        <div className="flex">
                                            <div className="w-full my-auto py-4 md:py-2">
                                                <AppLink to={(props.onClickUrl && props.onClickUrl(i)) || ''}>
                                                    <>{i?.name}</>
                                                </AppLink>
                                            </div>
                                        </div>
                                    </If>
                                </li>
                            )
                        })}
                    </>
                </ComponentState>
            </ul>
            {searchParams.has(DELETE_HASH) ? (
                <ConfirmationModal text="request.delete.confirmation" name={DELETE_HASH} onReject onConfirm={() => onDelete()}/>
            ) : <></>}
        </>
    );
};

export default HarvestTemplateDatalist;
